// src/services/TokenStorage.js - Token storage utilities
// Extracted from useTokenManager.js to follow separation of concerns
import logger from './logger';

const TokenStorage = {
  // ===== PHASE 3: FETCH TOKENS FROM SUPABASE =====
  // Fetch platform tokens from backend (which reads from Supabase)
  // Backend auto-refreshes tokens if expired
  async getPlatformTokens() {
    try {
      // Get Supabase auth token to send with request
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        logger.warn('No Supabase session, cannot fetch platform tokens');
        throw new Error('No Supabase session');
      }

      // Import config for API base URL
      const { API_BASE_URL } = await import('../config');

      // Call backend endpoint with Supabase auth header
      const response = await fetch(`${API_BASE_URL}/api/platform/tokens`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Supabase-Token': session.access_token
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data && data.access_token) {
        logger.success(`Fetched ${data.platform} tokens from Supabase`);
        return {
          platform: data.platform,
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
          expiry: data.expires_at
        };
      }

      throw new Error('No tokens in response');
    } catch (error) {
      logger.warn('Failed to fetch tokens from Supabase:', error.message);

      // Fallback to localStorage for backward compatibility
      const spotifyTokens = this.getSpotifyTokens();
      if (spotifyTokens.accessToken) {
        logger.debug('Using Spotify tokens from localStorage (fallback)');
        return {
          platform: 'spotify',
          ...spotifyTokens
        };
      }

      const youtubeTokens = this.getYouTubeTokens();
      if (youtubeTokens.accessToken) {
        logger.debug('Using YouTube tokens from localStorage (fallback)');
        return {
          platform: 'youtube',
          ...youtubeTokens
        };
      }

      throw new Error('No tokens available');
    }
  },

  // ===== EXISTING LOCALSTORAGE METHODS (KEPT FOR FALLBACK) =====
  // Spotify tokens
  setSpotifyTokens: (accessToken, refreshToken, expiresIn) => {
    localStorage.setItem('spotify_access_token', accessToken);
    localStorage.setItem('spotify_refresh_token', refreshToken);
    const expiry = Date.now() + (expiresIn * 1000);
    localStorage.setItem('spotify_token_expiry', expiry.toString());
    return expiry;
  },
  
  getSpotifyTokens: () => ({
    accessToken: localStorage.getItem('spotify_access_token'),
    refreshToken: localStorage.getItem('spotify_refresh_token'),
    expiry: localStorage.getItem('spotify_token_expiry') ? parseInt(localStorage.getItem('spotify_token_expiry')) : null
  }),
  
  clearSpotifyTokens: () => {
    localStorage.removeItem('spotify_access_token');
    localStorage.removeItem('spotify_refresh_token');
    localStorage.removeItem('spotify_token_expiry');
  },
  
  // YouTube tokens
  setYouTubeTokens: (accessToken, refreshToken, expiresIn) => {
    localStorage.setItem('youtube_access_token', accessToken);
    localStorage.setItem('youtube_refresh_token', refreshToken);
    const expiry = Date.now() + (expiresIn * 1000);
    localStorage.setItem('youtube_token_expiry', expiry.toString());
    return expiry;
  },
  
  getYouTubeTokens: () => ({
    accessToken: localStorage.getItem('youtube_access_token'),
    refreshToken: localStorage.getItem('youtube_refresh_token'),
    expiry: localStorage.getItem('youtube_token_expiry') ? parseInt(localStorage.getItem('youtube_token_expiry')) : null
  }),
  
  clearYouTubeTokens: () => {
    localStorage.removeItem('youtube_access_token');
    localStorage.removeItem('youtube_refresh_token');
    localStorage.removeItem('youtube_token_expiry');
  },

  // Clear all platform tokens
  clearAllTokens: () => {
    TokenStorage.clearSpotifyTokens();
    TokenStorage.clearYouTubeTokens();
  }
};

export default TokenStorage;